import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { User, Upload, Image, Camera, Shuffle, Loader2 } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { uploadsService } from "@/services/uploadsService";

type ProfilePictureUploadProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function ProfilePictureUpload({
  isOpen,
  onOpenChange,
}: ProfilePictureUploadProps) {
  const { user, updateUserProfile } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [newAvatarUrl, setNewAvatarUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadMode, setUploadMode] = useState<'file' | 'url' | 'generate'>('file');

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file (PNG, JPG, etc.)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Please select an image smaller than 5MB");
      return;
    }

    setIsUploading(true);

    try {
      // Upload via uploadsService - returns { url, key }
      const response = await uploadsService.uploadAvatar(file);
      
      if (!response?.url) {
        throw new Error('Upload succeeded but no URL returned');
      }
      
      setNewAvatarUrl(response.url);
      toast.success("Photo uploaded successfully!");

    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error("Failed to upload your photo. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleProfileUpdate = async () => {
    if (!newAvatarUrl) {
      toast.error("Please select or upload a profile picture");
      return;
    }

    setIsUploading(true);
    try {
      // Update local user state (which will sync to backend)
      await updateUserProfile({ avatarUrl: newAvatarUrl });
      
      toast.success("Profile picture updated successfully");
      onOpenChange(false);
      setNewAvatarUrl("");
    } catch (error) {
      console.error("Error updating profile picture:", error);
      toast.error("An error occurred while updating your profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  // Generate random avatar via backend
  const generateRandomAvatar = async () => {
    setIsGenerating(true);
    try {
      // uploadsService returns response.data.data directly (already unwrapped by apiService)
      const response = await uploadsService.generateAvatar('svg');
      const randomAvatarUrl = response?.avatar_url || response?.avatarUrl;
      
      if (!randomAvatarUrl) {
        throw new Error('No avatar URL returned from server');
      }
      
      setNewAvatarUrl(randomAvatarUrl);
      setUploadMode('generate');
    } catch (error) {
      console.error('Error generating avatar:', error);
      toast.error("Failed to generate avatar. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Update Profile Picture</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-4">
          {/* Current Preview */}
          <div className="flex justify-center">
            <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
              <AvatarImage src={newAvatarUrl || user?.avatarUrl} />
              <AvatarFallback className="text-3xl bg-gradient-to-br from-primary/20 to-primary/40">
                <User className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Upload Options */}
          <div className="space-y-3">
            {/* File Upload */}
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <label htmlFor="file-upload" className="cursor-pointer block">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      {isUploading ? (
                        <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                      ) : (
                        <Camera className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm">Upload a photo</h3>
                      <p className="text-xs text-muted-foreground">
                        {isUploading ? 'Uploading your photo...' : 'Choose a photo from your device (PNG, JPG, max 5MB)'}
                      </p>
                    </div>
                    <Upload className="h-4 w-4 text-muted-foreground" />
                  </div>
                </label>
                <input
                  ref={fileInputRef}
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
              </CardContent>
            </Card>

            {/* Generate Random Avatar */}
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <button 
                  onClick={generateRandomAvatar}
                  disabled={isGenerating}
                  className="w-full text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-full">
                      {isGenerating ? (
                        <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
                      ) : (
                        <Shuffle className="h-5 w-5 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm">Generate avatar</h3>
                      <p className="text-xs text-muted-foreground">
                        {isGenerating ? 'Generating your avatar...' : 'Create a fun, randomized avatar illustration'}
                      </p>
                    </div>
                  </div>
                </button>
              </CardContent>
            </Card>

            {/* URL Input */}
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Image className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">Enter image URL</h3>
                    <p className="text-xs text-muted-foreground">
                      Paste a link to an image from the web
                    </p>
                  </div>
                </div>
                <Input
                  placeholder="https://example.com/image.jpg"
                  value={uploadMode === 'url' ? newAvatarUrl : ''}
                  onChange={(e) => {
                    setNewAvatarUrl(e.target.value);
                    setUploadMode('url');
                  }}
                  className="mt-2"
                />
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleProfileUpdate}
              disabled={!newAvatarUrl || isUploading}
              className="flex-1"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Update Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
